import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Sparkle, Image as ImageIcon, LinkSimple, TextAa, Trash, Flask, Warning, ArrowRight, Clock } from "@phosphor-icons/react";
import DevilsAdvocateBlock from "@/components/DevilsAdvocateBlock";
import { toast } from "sonner";

const TABS = [
    { v: "url", label: "URL", Icon: LinkSimple },
    { v: "image", label: "Imagen", Icon: ImageIcon },
    { v: "text", label: "Descripción", Icon: TextAa },
];

export default function ProductAnalyzer() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("url");
    const [url, setUrl] = useState("");
    const [description, setDescription] = useState("");
    const [imageBase64, setImageBase64] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const fileRef = useRef(null);

    const loadHistory = () => api.get("/products").then((r) => setHistory(r.data));
    useEffect(() => { loadHistory(); }, []);

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 8 * 1024 * 1024) { toast.error("Imagen demasiado grande (máx. 8 MB)"); return; }
        const reader = new FileReader();
        reader.onload = () => setImageBase64(reader.result);
        reader.readAsDataURL(f);
    };

    const submit = async () => {
        if (tab === "url" && !url.trim()) return toast.error("Introduce una URL");
        if (tab === "image" && !imageBase64) return toast.error("Sube una imagen");
        if (tab === "text" && !description.trim()) return toast.error("Describe el producto");
        setSubmitting(true);
        try {
            const payload = {
                url: tab === "url" ? url : null,
                image_base64: tab === "image" ? imageBase64 : null,
                description: description || null,
            };
            const { data } = await api.post("/products/analyze", payload);
            setResult(data);
            // Poll if analyzing in background
            if (data.status === "analyzing") {
                toast.info("Analizando — Claude está pensando…");
                let tries = 0;
                const pollId = setInterval(async () => {
                    tries += 1;
                    try {
                        const r = await api.get(`/products/${data.id}`);
                        if (r.data.status !== "analyzing") {
                            clearInterval(pollId);
                            setResult(r.data);
                            setSubmitting(false);
                            if (r.data.status === "error") toast.error("Análisis falló: " + (r.data.error_message || ""));
                            else toast.success("Análisis completado");
                            loadHistory();
                        }
                    } catch {}
                    if (tries > 40) { clearInterval(pollId); setSubmitting(false); }
                }, 5000);
                return;
            }
            toast.success("Análisis completado");
            loadHistory();
            setSubmitting(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo analizar el producto");
            setSubmitting(false);
        }
    };

    const openHistorical = async (id) => {
        try {
            const { data } = await api.get(`/products/${id}`);
            setResult(data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            toast.error("No se pudo cargar el análisis");
        }
    };

    const deleteHistorical = async (id) => {
        if (!window.confirm("¿Eliminar este análisis?")) return;
        await api.delete(`/products/${id}`);
        toast.success("Eliminado");
        loadHistory();
    };

    const generateFormulaFromProduct = async () => {
        const improved = (result?.result || result)?.improved_formula;
        if (!improved?.components?.length) {
            toast.error("La propuesta mejorada no tiene componentes válidos");
            return;
        }
        const slugs = improved.components.map((c) => c.compound_slug).filter(Boolean);
        if (!slugs.length) {
            toast.error("Los componentes no tienen slugs reconocidos en el corpus");
            return;
        }
        setGenerating(true);
        try {
            const compHints = improved.components
                .map((c) => `- ${c.compound_name} ${c.dose_mg || ""}${c.unit || "mg"} · ${c.frequency || "1x/día"}`)
                .join("\n");
            const customPrompt =
                `Reproduce la fórmula mejorada propuesta a partir del análisis del producto "${(result?.result||result)?.product_name || "(sin nombre)"}".\n` +
                `Composición sugerida:\n${compHints}\n` +
                (improved.daily_protocol ? `Pauta diaria propuesta: ${improved.daily_protocol}\n` : "") +
                (improved.rationale ? `Racional: ${improved.rationale}` : "");
            const { data } = await api.post("/formulas/generate", {
                mode: "validate",
                objective: improved.objective || improved.title || "Fórmula derivada de análisis de producto",
                target_population: "adultos sanos 20-90 años",
                preferred_compound_slugs: slugs,
                custom_prompt: customPrompt,
                prefer_max_dose: false,
            });
            toast.success("Generando fórmula — te llevamos al detalle");
            navigate(`/formulas/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo iniciar la generación de fórmula");
        } finally {
            setGenerating(false);
        }
    };

    const r = result?.result || result;  // result object holds {result: {...}} when persisted, or the raw analysis
    const isAnalyzing = result?.status === "analyzing";

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow="Analizador de productos · Claude Sonnet 4.5"
                title={<>Auditar y <em className="text-[var(--sage)]">superar</em> productos comerciales.</>}
                lead="Sube la imagen de una etiqueta, pega la URL de un producto o describe la composición. La plataforma extrae los principios activos, evalúa sus dosis y propone una fórmula mejorada basada en evidencia."
            />

            <div className="px-8 md:px-12 py-8 max-w-6xl grid lg:grid-cols-[1.3fr_1fr] gap-10">
                {/* Input panel */}
                <div className="space-y-6">
                    <div className="border hairline bg-white p-2 flex gap-2 w-fit" data-testid="analyzer-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.v}
                                onClick={() => setTab(t.v)}
                                className={`px-4 py-2 text-sm inline-flex items-center gap-2 ${tab === t.v ? "bg-[var(--sage)] text-white" : "text-[var(--ink-2)] hover:bg-[var(--cream)]"}`}
                                data-testid={`analyzer-tab-${t.v}`}
                            >
                                <t.Icon size={14}/> {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === "url" && (
                        <Field label="URL del producto comercial">
                            <input value={url} onChange={(e)=>setUrl(e.target.value)} placeholder="https://www.farmaciaonline.com/producto..." className="i" data-testid="analyzer-url-input"/>
                        </Field>
                    )}

                    {tab === "image" && (
                        <Field label="Imagen de la etiqueta (jpg/png/webp, máx. 8 MB)">
                            <div className="border hairline bg-white p-6 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-[var(--cream)]" onClick={()=>fileRef.current?.click()} data-testid="analyzer-image-drop">
                                {imageBase64 ? (
                                    <img src={imageBase64} alt="preview" className="max-h-64 object-contain"/>
                                ) : (
                                    <>
                                        <ImageIcon size={32} className="text-[var(--ink-3)]"/>
                                        <div className="text-sm text-[var(--ink-2)]">Haz clic o arrastra una imagen aquí</div>
                                    </>
                                )}
                                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="analyzer-image-input"/>
                            </div>
                            {imageBase64 && (
                                <button onClick={(e)=>{e.stopPropagation(); setImageBase64("");}} className="mt-2 text-xs text-[var(--terra-2)] inline-flex items-center gap-1" data-testid="analyzer-image-clear"><Trash size={12}/> Quitar imagen</button>
                            )}
                        </Field>
                    )}

                    {tab === "text" && (
                        <Field label="Descripción libre del producto">
                            <textarea rows={6} value={description} onChange={(e)=>setDescription(e.target.value)} className="i resize-none" placeholder="Ej: Producto X con magnesio 200mg, vitamina B6 5mg, taurina 100mg, etiquetado anti-estrés…" data-testid="analyzer-description-input"/>
                        </Field>
                    )}

                    {tab !== "text" && (
                        <Field label="Nota adicional (opcional)">
                            <input value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="Indicio para Claude (ej: 'centrar análisis en biodisponibilidad')" className="i" data-testid="analyzer-hint-input"/>
                        </Field>
                    )}

                    <button onClick={submit} disabled={submitting} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="analyzer-submit-button">
                        <Sparkle size={16} weight="bold"/> {submitting ? "Analizando producto…" : "Analizar producto"}
                    </button>
                </div>

                {/* History panel */}
                <aside className="border hairline bg-white p-5 self-start">
                    <div className="label-eyebrow mb-3">Historial de análisis · {history.length}</div>
                    {history.length === 0 ? (
                        <div className="text-xs text-[var(--ink-3)] py-4">Aún no has analizado ningún producto.</div>
                    ) : (
                        <ul className="space-y-2 max-h-[480px] overflow-y-auto">
                            {history.map((h) => (
                                <li key={h.id} className="border hairline p-3 flex items-start justify-between gap-2" data-testid={`analyzer-history-${h.id}`}>
                                    <button onClick={()=>openHistorical(h.id)} className="text-left flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{h.result?.product_name || h.source_url || "(sin nombre)"}</div>
                                        <div className="text-xs text-[var(--ink-3)] mono mt-0.5">{h.source_kind} · {new Date(h.created_at).toLocaleDateString("es-ES")}</div>
                                    </button>
                                    <button onClick={()=>deleteHistorical(h.id)} className="text-[var(--terra-2)] hover:opacity-70" data-testid={`analyzer-history-delete-${h.id}`}><Trash size={14}/></button>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>
            </div>

            {/* Results */}
            {isAnalyzing && (
                <div className="px-8 md:px-12 pb-8 max-w-6xl" data-testid="analyzer-pending">
                    <div className="border hairline bg-white p-6 flex items-center gap-4">
                        <Sparkle size={24} className="text-[var(--sage)] animate-pulse" weight="duotone"/>
                        <div>
                            <div className="font-display text-lg tracking-tight">Analizando producto…</div>
                            <p className="text-sm text-[var(--ink-2)]">Claude evalúa la composición y diseña la versión mejorada. 30-120 s.</p>
                        </div>
                    </div>
                </div>
            )}
            {r && !isAnalyzing && (
                <div className="px-8 md:px-12 pb-12 max-w-6xl" data-testid="analyzer-result">
                    <div className="border hairline bg-[var(--cream)] p-6 mb-6">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <div className="label-eyebrow mb-1">Resultado</div>
                                <h2 className="font-display text-2xl tracking-tight">{r.product_name || "Producto sin nombre"} {r.brand && <span className="text-[var(--ink-2)] text-base">· {r.brand}</span>}</h2>
                            </div>
                            <span className={`citation-badge uppercase ${r.confidence === "high" ? "bg-[var(--sage)] text-white border-[var(--sage)]" : r.confidence === "low" ? "bg-[var(--terra-2)] text-white border-[var(--terra-2)]" : "bg-[var(--amber)] text-white border-[var(--amber)]"}`}>
                                Confianza {r.confidence || "n/d"}
                            </span>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6">
                        {r.composition_detected?.length > 0 && (
                            <Section title="Composición detectada">
                                <table className="data-table w-full">
                                    <thead><tr><th>Ingrediente</th><th>Dosis</th><th>Comentario</th></tr></thead>
                                    <tbody>
                                        {r.composition_detected.map((c, i) => (
                                            <tr key={i}><td>{c.ingredient}</td><td className="mono text-xs">{c.dose_mg ? `${c.dose_mg} ${c.unit || "mg"}` : "—"}</td><td className="text-xs text-[var(--ink-2)]">{c.comment}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Section>
                        )}
                        {r.weaknesses?.length > 0 && (
                            <Section title="Debilidades">
                                <ul className="text-sm space-y-1.5 text-[var(--terra-2)]">{r.weaknesses.map((w, i) => <li key={i}>• {w}</li>)}</ul>
                            </Section>
                        )}
                        {r.strengths?.length > 0 && (
                            <Section title="Fortalezas">
                                <ul className="text-sm space-y-1.5">{r.strengths.map((w, i) => <li key={i}>• {w}</li>)}</ul>
                            </Section>
                        )}
                    </div>

                    <div className="mt-6">
                        <DevilsAdvocateBlock
                            devilsAdvocate={r.devils_advocate}
                            expectedComplications={r.expected_complications}
                            populationsWhereMightFail={r.populations_where_might_fail}
                            testid="analyzer-devils-advocate"
                        />
                    </div>

                    {r.improved_formula && (
                        <div className="mt-6 border hairline bg-white p-6" data-testid="analyzer-improved-formula">
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                <div className="label-eyebrow flex items-center gap-2"><Flask size={14}/> Fórmula mejorada propuesta {typeof r.self_quality_score === "number" && <span className="ml-2 mono text-xs px-2 py-0.5 bg-[var(--sage)] text-white" data-testid="analyzer-quality-score">Calidad {r.self_quality_score}/10</span>}</div>
                                {r.improved_formula?.components?.length > 0 && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button
                                            onClick={generateFormulaFromProduct}
                                            disabled={generating}
                                            className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50"
                                            data-testid="analyzer-generate-formula-direct"
                                        >
                                            <Sparkle size={12}/> {generating ? "Iniciando…" : "Generar fórmula"} <ArrowRight size={12}/>
                                        </button>
                                        <Link
                                            to={`/formulas/nuevo?preferred=${(r.improved_formula.components || []).map(c => c.compound_slug).filter(Boolean).join(",")}`}
                                            className="px-3 py-2 border hairline bg-white text-xs hover:bg-[var(--cream)] text-[var(--ink-2)] inline-flex items-center gap-1"
                                            data-testid="analyzer-build-formula"
                                        >Editar antes</Link>
                                    </div>
                                )}
                            </div>
                            <h3 className="font-display text-xl tracking-tight">{r.improved_formula.title}</h3>
                            {r.improved_formula.rationale && <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">{r.improved_formula.rationale}</p>}
                            <table className="data-table w-full mt-4">
                                <thead><tr><th>Principio activo</th><th>Dosis</th><th>Frecuencia</th><th>Justificación</th></tr></thead>
                                <tbody>
                                    {(r.improved_formula.components || []).map((c, i) => (
                                        <tr key={i}>
                                            <td className="font-medium">{c.compound_name}</td>
                                            <td className="mono text-xs">{c.dose_mg} {c.unit || "mg"}</td>
                                            <td className="text-xs">{c.frequency}</td>
                                            <td className="text-xs text-[var(--ink-2)]">{c.rationale}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Daily protocol — pauta de tratamiento diaria propuesta */}
                            {r.improved_formula.daily_protocol && (
                                <div className="mt-5 border hairline bg-[var(--cream)] p-4" data-testid="analyzer-daily-protocol">
                                    <div className="label-eyebrow mb-2 text-[var(--sage)] flex items-center gap-2"><Clock size={14}/> Pauta de tratamiento diaria</div>
                                    <p className="text-sm leading-relaxed whitespace-pre-line">{r.improved_formula.daily_protocol}</p>
                                    {(r.improved_formula.duration_weeks || r.improved_formula.monitoring) && (
                                        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs text-[var(--ink-2)]">
                                            {r.improved_formula.duration_weeks && (
                                                <div><span className="label-eyebrow mr-1">Ciclo:</span> {r.improved_formula.duration_weeks} semanas (revaluable).</div>
                                            )}
                                            {r.improved_formula.monitoring && (
                                                <div><span className="label-eyebrow mr-1">Seguimiento:</span> {r.improved_formula.monitoring}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Improvement map: how each weakness/devil is addressed */}
                            {r.improvement_map?.length > 0 && (
                                <div className="mt-6 border-t hairline pt-5" data-testid="analyzer-improvement-map">
                                    <div className="label-eyebrow mb-3 text-[var(--sage)]">Cómo la versión mejorada resuelve cada crítica</div>
                                    <table className="data-table w-full">
                                        <thead><tr><th>Categoría</th><th>Problema detectado</th><th>Cómo se resuelve</th></tr></thead>
                                        <tbody>
                                            {r.improvement_map.map((m, i) => (
                                                <tr key={i}>
                                                    <td><span className={`citation-badge ${m.category === "devils_advocate" ? "bg-[var(--terra-2)] text-white border-[var(--terra-2)]" : m.category === "expected_complication" ? "bg-[var(--amber)] text-white border-[var(--amber)]" : ""}`}>{m.category}</span></td>
                                                    <td className="text-xs text-[var(--ink-2)]">{m.issue}</td>
                                                    <td className="text-xs">{m.addressed_by}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Residual limitations: honest self-criticism */}
                            {r.residual_limitations?.length > 0 ? (
                                <div className="mt-6 border-t hairline pt-5" data-testid="analyzer-residual-limitations">
                                    <div className="label-eyebrow mb-3 text-[var(--terra-2)]">Limitaciones residuales (huecos del estado del arte)</div>
                                    <ul className="text-sm space-y-1.5 text-[var(--ink-2)]">
                                        {r.residual_limitations.map((x, i) => <li key={i}>• {x}</li>)}
                                    </ul>
                                </div>
                            ) : typeof r.self_quality_score === "number" && r.self_quality_score >= 9 ? (
                                <div className="mt-6 border-t hairline pt-5 text-sm text-[var(--sage)]" data-testid="analyzer-unimprovable">
                                    Sin limitaciones residuales relevantes según la evidencia actual — fórmula considerada óptima.
                                </div>
                            ) : null}
                        </div>
                    )}

                    {r.competitors_found?.length > 0 && (
                        <div className="mt-6">
                            <Section title="Competidores detectados">
                                <ul className="text-sm space-y-2">
                                    {r.competitors_found.map((c, i) => (
                                        <li key={i} className="border hairline p-3">
                                            <a href={c.url} target="_blank" rel="noreferrer" className="text-[var(--sage)] hover:underline font-medium">{c.title}</a>
                                            <p className="text-xs text-[var(--ink-2)] mt-1 line-clamp-2">{c.snippet}</p>
                                        </li>
                                    ))}
                                </ul>
                            </Section>
                        </div>
                    )}

                    {r.confidence === "low" && (!r.composition_detected || r.composition_detected.length === 0) && (
                        <div className="mt-6 border hairline bg-[var(--cream)] p-5 text-sm flex items-start gap-3" data-testid="analyzer-low-confidence">
                            <Warning size={20} className="text-[var(--terra-2)] flex-shrink-0 mt-0.5" weight="duotone"/>
                            <p>No se pudo extraer la composición con suficiente claridad. Prueba con una imagen de mayor resolución o pega la URL del fabricante.</p>
                        </div>
                    )}
                </div>
            )}
            <style>{`.i{width:100%;padding:10px 12px;border:1px solid var(--line);background:#fff;font-size:.875rem;outline:none}.i:focus{border-color:var(--sage)}`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (<div><label className="label-eyebrow block mb-2">{label}</label>{children}</div>);
}
function Section({ title, children }) {
    return (<div className="border hairline bg-white p-5"><div className="label-eyebrow mb-3">{title}</div>{children}</div>);
}
